/**
 * @file LabelSheetMiniPreview.tsx
 * @description Миниатюра листа A4 с раскладкой наклеек по текущим полям и
 *   зазорам (phase19.22, W17-5b; ui-design §3, 1280).
 *
 * Не чертёж, а подсказка глазами: поле слишком большое — сетка уезжает за
 * край листа, и это видно до печати пробной страницы. Координаты — проценты
 * от A4 (210×297 мм), наклейки — абсолютные прямоугольники.
 */

import type { LabelSheetDto, LabelSheetMargins } from "../../../lib/api-labels";

const A4_W = 210;
const A4_H = 297;

interface Props {
  sheet: Pick<LabelSheetDto, "widthMm" | "heightMm" | "cols" | "rows" | "title">;
  margins: LabelSheetMargins;
}

export function LabelSheetMiniPreview({ sheet, margins }: Props) {
  const cells: { left: number; top: number; key: string }[] = [];
  for (let r = 0; r < sheet.rows; r += 1) {
    for (let c = 0; c < sheet.cols; c += 1) {
      cells.push({
        key: `${r}-${c}`,
        left: margins.marginLeftMm + c * (sheet.widthMm + margins.gapXMm),
        top: margins.marginTopMm + r * (sheet.heightMm + margins.gapYMm),
      });
    }
  }

  return (
    <div
      role="img"
      aria-label={`Раскладка листа: ${sheet.title}, ${sheet.cols} × ${sheet.rows}`}
      className="relative w-24 shrink-0 overflow-hidden rounded-sm bg-surface-lowest ring-ghost"
      style={{ aspectRatio: `${A4_W} / ${A4_H}` }}
    >
      {cells.map((cell) => (
        <span
          key={cell.key}
          className="absolute rounded-[1px] bg-primary/25"
          style={{
            left: `${(cell.left / A4_W) * 100}%`,
            top: `${(cell.top / A4_H) * 100}%`,
            width: `${(sheet.widthMm / A4_W) * 100}%`,
            height: `${(sheet.heightMm / A4_H) * 100}%`,
            boxShadow: "inset 0 0 0 0.5px var(--primary)",
          }}
        />
      ))}
    </div>
  );
}
